'use client';

import { GiHamburgerMenu } from '@react-icons/all-files/gi/GiHamburgerMenu';
import { IoMdClose } from '@react-icons/all-files/io/IoMdClose';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState } from 'react';
import { IPromoPage } from '../../types';
import { StyledLandingPage } from './promoPageComp.styles';
import logo from '../../../public/assets/cn-logo-light.svg';
import logoWhite from '../../../public/assets/cn-logo-dark.svg';
import { CommonNinjaWidgetRenderer } from '../CommonNinjaWidgetRenderer/commonNinjaWidgetRenderer.comp';

interface IPromoPageCompProps {
	promoPage: IPromoPage;
}

export const PromoPageComp = ({ promoPage }: IPromoPageCompProps) => {
	const [menuClicked, setMenuClicked] = useState<boolean>(false);

	function getBackgroundValue(
		colors: string | string[],
		deg: number = 90
	): string {
		if (typeof colors === 'string') {
			return colors;
		}

		let background = colors[0];

		if (colors.length > 1) {
			const steps = colors.length;
			const str: string = colors
				.map((color, i) => {
					let step = 100;

					if (i === steps - 1) {
						step = 100;
					} else if (i === 0) {
						step = 0;
					} else {
						step = (100 / steps) * i;
					}

					return `${color} ${step}%`;
				})
				.join(', ');

			background = `linear-gradient(${deg}deg, ${str})`;
		}
		return background;
	}

	return (
		<StyledLandingPage>
			<section
				style={{
					background: getBackgroundValue(
						promoPage?.heroSectionBackground || '#fff'
					),
				}}
				className="hero-section"
			>
				<header>
					<div className="nav-wrapper">
						<figure className="logo">
							<Link href="/">
								<Image
									width={200}
									height={30}
									src={promoPage?.logoColor === 'light' ? logoWhite : logo}
									alt="Common Ninja"
								/>
							</Link>
						</figure>
						<nav className={`${menuClicked ? 'show' : ''}`}>
							{promoPage?.navbarLinks.length > 0 &&
								promoPage.navbarLinks.map((link) => (
									<Link
										key={link.url}
										style={{ color: promoPage?.navbarLinksColor || '#000' }}
										href={`${(link.url[0] === '/' || link.url.includes('http')) ? `${link.url}` : `/${link.url}`
											}`}
									>
										{link.text}
									</Link>
								))}
							<Link
								className="try-now"
								style={{
									background: getBackgroundValue(
										promoPage?.buttonBackground || 'transparent'
									),
									color: promoPage?.buttonTextColor || '#000',
								}}
								href={`${(promoPage?.buttonLink[0] === '/' || promoPage?.buttonLink.includes('http'))
									? `${promoPage?.buttonLink}`
									: `/${promoPage?.buttonLink}`
									}`}
							>
								{promoPage?.buttonText}
							</Link>
						</nav>
						<GiHamburgerMenu
							style={{ color: promoPage?.navbarLinksColor || '#000' }}
							onClick={() => setMenuClicked(true)}
							className="hamburger"
						/>
						<IoMdClose
							onClick={() => setMenuClicked(false)}
							className={`${menuClicked ? 'show' : ''} close`}
						/>
					</div>
				</header>
				<div className="hero-content-container">
					<div className="details-wrapper">
						<h1 style={{ color: promoPage?.heroTitleColor || '#000' }}>
							{promoPage?.name}
						</h1>
						{promoPage?.subTitle && (
							<h3 style={{ color: promoPage?.subTitleColor || '#000' }}>
								{promoPage?.subTitle}
							</h3>
						)}
						<div
							className="teaser"
							style={{ color: promoPage?.heroTextColor || '#000' }}
							dangerouslySetInnerHTML={{
								__html: promoPage.teaser.replace(/\n/g, '<br />'),
							}}
						></div>
						<Link
							style={{
								background: getBackgroundValue(
									promoPage?.buttonBackground || 'transparent'
								),
								color: promoPage?.buttonTextColor || '#000',
							}}
							href={`${(promoPage?.buttonLink[0] === '/' || promoPage?.buttonLink.includes('http'))
								? `${promoPage?.buttonLink}`
								: `/${promoPage?.buttonLink}`
								}`}
						>
							{promoPage?.buttonText}
						</Link>
					</div>
					<Image
						width={600}
						height={700}
						src={promoPage?.hero || ''}
						alt={`${promoPage.name} hero image`}
					/>
				</div>
			</section>
			{promoPage?.features.length > 0 &&
				<section
					style={{
						background: getBackgroundValue(
							promoPage?.featureSectionBackground || '#fff'
						),
					}}
					className="features-section"
				>
					<div className="features-content-wrapper">
						{promoPage.features.map((feature) => (
							<div key={`feature ${feature.title}`} className="feature">
								<Image
									width={600}
									height={450}
									src={feature.imgUrl}
									alt={`${feature.title} feature`}
								/>
								<div className="feature-details">
									<h2
										style={{ color: promoPage?.featuresTitleColor || '#000' }}
									>
										{feature.title}
									</h2>
									<p style={{ color: promoPage?.featuresTextColor || '#000' }}>
										{feature.text}
									</p>
								</div>
							</div>
						))}
					</div>
				</section>
			}
			{promoPage?.keyBenefits.length > 0 && (
				<section
					style={{
						background: getBackgroundValue(
							promoPage?.benefitSectionBackground || '#fff'
						),
					}}
					className="benefits-section"
				>
					<div className="benefits-content-wrapper">
						<h3 style={{ color: promoPage?.benefitsTitleColor || '#000' }}>
							{promoPage.benefitsTitle}
						</h3>
						<p style={{ color: promoPage?.benefitsTextColor || '#000' }}>
							{promoPage.benefitsDescription}
						</p>
						<div className="benefit-cards">
							{promoPage.keyBenefits.map((benefit) => (
								<div
									key={`benefit-${benefit?.title || ''}`}
									style={{
										background:
											promoPage?.benefitCardsBackground?.[0] || '#000',
									}}
									className="card"
								>
									<h4
										style={{ color: promoPage?.benefitsTitleColor || '#000' }}
									>
										{benefit?.title}
									</h4>
									<p style={{ color: promoPage?.benefitsTextColor || '#000' }}>
										{benefit?.text}
									</p>
									{benefit?.buttonText && (
										<Link
											target="_blank"
											href={benefit?.url || ''}
											style={{
												background:
													getBackgroundValue(
														promoPage?.benefitButtonBackground || ''
													) || 'transparent',
												color: promoPage?.benefitButtonTextColor || '#000',
											}}
										>
											{benefit.buttonText}
										</Link>
									)}
								</div>
							))}
						</div>
					</div>
				</section>
			)}
			{promoPage?.examples.length > 0 && (
				<section
					style={{
						background: getBackgroundValue(
							promoPage?.exampleSectionBackground || '#fff'
						),
					}}
					className="examples-section"
				>
					<div className="examples-content-wrapper">
						{promoPage.examples.map((example) => (
							<div
								key={`example ${example.title}`}
								className={`example ${example.position}`}
							>
								{example.pluginId ? (
									<div className="html">
										<CommonNinjaWidgetRenderer widgetId={example.pluginId} />
									</div>
								) : (
									<Image
										width={1320}
										height={742.5}
										src={example.imgUrl}
										alt={`${example.title} example`}
									/>
								)}
								<div className="example-details">
									<h2
										style={{ color: promoPage?.examplesTitleColor || '#000' }}
									>
										{example.title}
									</h2>
									<p style={{ color: promoPage?.examplesTextColor || '#000' }}>
										{example.text}
									</p>
								</div>
							</div>
						))}
					</div>
				</section>
			)}
		</StyledLandingPage>
	);
};
